/* eslint-disable jsx-a11y/anchor-is-valid */
import { FontFa, Table } from "../../../../../../../components";
import { ColumnsType } from "antd/es/table";
import { usePermission } from "../../../hooks/use-permission";
import { useRole } from "../../../hooks/use-role";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "../../../../../../../hook/use-search-params";
import { Popconfirm, Space, Tooltip, Typography } from "antd";
import { HiPencilSquare } from "react-icons/hi2";
import "./permission-group.scss";
import { RolePermissionService } from "../../../../../../../services/config/permission-config/role-permission";
import { useQueryClient } from "@tanstack/react-query";
import { CreateNotification } from "../../../../../../../components/notifier";

type ColumnsDataType = {
  title: string;
  key?: string;
  create: boolean;
  view: boolean;
  edit: boolean;
  delete: boolean;
};

export const PermissionGroup = () => {
  const { listPermissions } = usePermission();
  const { listRolePermission, isLoading } = useRole();
  const { searchParams } = useSearchParams();
  const menuId = searchParams.get("menuId") || "54";
  const tab = searchParams.get("tab");

  const [editingKey, setEditingKey] = useState("");
  const refDataSource = useRef<ColumnsDataType[]>([]);
  const [dataSource, setDataSource] = useState<ColumnsDataType[]>([]);
  const queryClient = useQueryClient();

  const isEditing = (record: ColumnsDataType) => record.key === editingKey;

  const save = async (record: ColumnsDataType) => {
    const fields = {
      roleId: +menuId,
      permissionId: +record.key!,
      c: record.create,
      r: record.view,
      u: record.edit,
      d: record.delete,
      description: "",
      note: "",
      id: 0,
    };
    const res = await RolePermissionService.updateRolePermission(fields);
    if (res.statusCode === 200) {
      CreateNotification({
        title: "Thành công",
        body: "Cập nhật quyền thành công",
        icon: "/icons/success.png",
      });
    }
    setEditingKey("");
    queryClient.invalidateQueries({ queryKey: ["config-role"] });
  };
  const cancel = () => {
    setEditingKey("");
    setDataSource(refDataSource.current);
  };

  const edit = (record: ColumnsDataType) => {
    const id = record.key;
    setEditingKey(id!);
  };

  const handleCRUDClick = (record: any, fieldName: string) => {
    const updatedData = [...dataSource]; // Replace with your actual data source
    const updatedRecord = { ...record, [fieldName]: !record[fieldName] };
    const index = updatedData.findIndex((item) => item.key === record.key);

    if (index !== -1) {
      updatedData[index] = updatedRecord;
      setDataSource(updatedData);
    }
  };

  const columns: ColumnsType<ColumnsDataType> = [
    {
      title: "Quy trình",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Tạo",
      dataIndex: "create",
      key: "create",
      render: (isCreate: Boolean, record: ColumnsDataType) => (
        <Typography.Link
          disabled={editingKey !== record.key || +record.key! > 2}
          onClick={() => (editingKey ? handleCRUDClick(record, "create") : null)}
        >
          <FontFa
            className={editingKey ? "hoverable-button" : ""}
            size="lg"
            name={+record.key! > 2 ? "faBan" : "faCheckCircle"}
            color={isCreate ? "#44F194" : "#E1DBDD"}
          />
        </Typography.Link>
      ),
      align: "center" as const,
    },
    {
      title: "Xem",
      dataIndex: "view",
      key: "view",
      render: (isView: Boolean, record: ColumnsDataType) => (
        <Typography.Link
          disabled={editingKey !== record.key || record.key === "19" || record.key === "20"}
          onClick={() => (editingKey ? handleCRUDClick(record, "view") : null)}
        >
          <FontFa
            className={editingKey ? "hoverable-button" : ""}
            size="lg"
            name={(record.key === "19" || record.key === "20") ? "faBan" : "faCheckCircle"}
            color={isView ? "#44F194" : "#E1DBDD"}
          />
        </Typography.Link>
      ),
      align: "center" as const,
    },
    {
      title: "Chỉnh sửa",
      dataIndex: "edit",
      key: "edit",
      render: (isEdit: Boolean, record: ColumnsDataType) => (
        <Typography.Link
          disabled={editingKey !== record.key}
          onClick={() => (editingKey ? handleCRUDClick(record, "edit") : null)}
        >
          <FontFa
            className={editingKey ? "hoverable-button" : ""}
            size="lg"
            name="faCheckCircle"
            color={isEdit ? "#44F194" : "#E1DBDD"}
          />
        </Typography.Link>
      ),
      align: "center" as const,
    },
    {
      title: "Xoá",
      dataIndex: "delete",
      key: "delete",
      render: (isDelete: Boolean, record: ColumnsDataType) => (
        <Typography.Link
          disabled={editingKey !== record.key || record.key === "19" || record.key === "20" || record.key === "21"}
          onClick={() => (editingKey ? handleCRUDClick(record, "delete") : null)}
        >
          <FontFa
            className={editingKey ? "hoverable-button" : ""}
            size="lg"
            name={(record.key === "19" || record.key === "20" || record.key === "21")  ? "faBan" : "faCheckCircle"}
            color={isDelete ? "#44F194" : "#E1DBDD"}
          />
        </Typography.Link>
      ),
      align: "center" as const,
    },
    {
      key: "action",
      dataIndex: "action",
      width: 100,
      align: "center" as const,
      render: (_: string, record: any) => {
        const editable = isEditing(record);
        return (
          <Space>
            {editable ? (
              <p>
                <Typography.Link onClick={() => save(record)} style={{ marginRight: 8 }}>
                  Lưu
                </Typography.Link>
                <Popconfirm title="Anh/Chị có muốn huỷ?" okText="Đồng ý" cancelText="Không" onConfirm={cancel}>
                  <a>Huỷ</a>
                </Popconfirm>
              </p>
            ) : (
              <Typography.Link disabled={!!editingKey} onClick={() => edit(record)}>
                <Tooltip title="Chỉnh sửa" color="blue">
                  <HiPencilSquare size={20} className="cursor-pointer" color={editingKey ? "#CEE3F3" : "#0ea5e9"} />
                </Tooltip>
              </Typography.Link>
            )}
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    const updatedDataSource = listPermissions.map((item2) => {
      const matchingPermission = _.find(
        listRolePermission(+menuId),
        (item1) => String(item1.permissionId) === item2.key
      );

      const combinedElement = {
        title: item2.title,
        key: item2.key,
        create: _.get(matchingPermission, "create", false),
        view: _.get(matchingPermission, "view", false),
        edit: _.get(matchingPermission, "edit", false),
        delete: _.get(matchingPermission, "delete", false),
      };
      return combinedElement;
    });
    refDataSource.current = updatedDataSource;
    setDataSource(updatedDataSource);
  }, [menuId, tab, listPermissions, listRolePermission]);

  return (
    <Table
      bordered
      dataSource={dataSource}
      columns={columns}
      rowClassName="editable-row"
      pagination={{ pageSize: 13 }}
      style={{ padding: "16px" }}
      loading={isLoading}
    />
  );
};
